import { Capacitor } from '@capacitor/core';
import {
  AppsFlyer,
  AFInit,
  AFEvent,
  AFConstants,
  OnDeepLink,
  OnConversionDataResult,
} from 'appsflyer-capacitor-plugin';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { getAppsFlyerDevKey, getAppsFlyerAppId, isProduction, isDevelopment } from '../config/env';

// Configuration constants
// For iOS: use App Store ID (numbers only)
// For Android: AppsFlyer will automatically use the app package name from capacitor.config.ts
const APPSFLYER_CONFIG = {
  devKey: getAppsFlyerDevKey(),
  appID: getAppsFlyerAppId(),
  isDebug: !isProduction(), // Enable debug mode for development only
  waitForATTUserAuthorization: 10, // iOS 14+ ATT timeout in seconds
  // minTimeBetweenSessions is optional - omitting to use SDK default behavior
  // Note: Setting this causes "Event timeout" errors during frequent app restarts in development
  registerOnDeepLink: true,
  registerConversionListener: true,
  registerOnAppOpenAttribution: false,
  deepLinkTimeout: 3000,
  useReceiptValidationSandbox: isDevelopment(), // Use sandbox for development
} as const;

// Event tracking types
interface AppsflyerEventData {
  [key: string]: string | number | boolean;
}

interface RevenueEventData {
  af_revenue: number;
  af_currency: string;
  af_receipt_id?: string;
  af_order_id?: string;
  af_content_id?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Sanitize event name for Firebase Analytics compatibility
 * Firebase rules:
 * - Max 40 characters
 * - Must start with a letter
 * - Only alphanumeric characters and underscores
 * - Cannot be a reserved event name
 */
function sanitizeFirebaseEventName(eventName: string): string {
  // Firebase reserved event names (common ones)
  const reservedNames = new Set([
    'ad_activeview',
    'ad_click',
    'ad_exposure',
    'ad_impression',
    'ad_query',
    'adunit_exposure',
    'app_clear_data',
    'app_exception',
    'app_remove',
    'app_update',
    'error',
    'first_open',
    'first_visit',
    'in_app_purchase',
    'notification_dismiss',
    'notification_foreground',
    'notification_open',
    'notification_receive',
    'os_update',
    'screen_view',
    'session_start',
    'user_engagement',
  ]);

  // If it's a reserved name, prefix with 'custom_'
  if (reservedNames.has(eventName.toLowerCase())) {
    eventName = `custom_${eventName}`;
  }

  // Replace invalid characters with underscores
  let sanitized = eventName.replace(/[^a-zA-Z0-9_]/g, '_');

  // Ensure it starts with a letter
  if (!/^[a-zA-Z]/.test(sanitized)) {
    sanitized = `event_${sanitized}`;
  }

  // Truncate to 40 characters
  if (sanitized.length > 40) {
    sanitized = sanitized.substring(0, 40);
  }

  return sanitized;
}

/**
 * Sanitize event parameters for Firebase Analytics compatibility
 * Firebase params:
 * - Keys must follow same rules as event names
 * - Values can be string, number, or boolean (no arrays or objects)
 * - Max 100 params per event
 * - Param names max 40 characters
 * - String values max 100 characters
 */
function sanitizeFirebaseParams(params: AppsflyerEventData): Record<string, string | number | boolean> {
  const sanitized: Record<string, string | number | boolean> = {};
  let paramCount = 0;

  for (const [key, value] of Object.entries(params)) {
    // Skip if we've hit the 100 param limit
    if (paramCount >= 100) {
      console.warn('[FIREBASE] Param limit reached (100), skipping remaining params');
      break;
    }

    // Sanitize key name
    let sanitizedKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
    if (!/^[a-zA-Z]/.test(sanitizedKey)) {
      sanitizedKey = `param_${sanitizedKey}`;
    }
    if (sanitizedKey.length > 40) {
      sanitizedKey = sanitizedKey.substring(0, 40);
    }

    // Sanitize value based on type
    if (typeof value === 'string') {
      // Truncate long strings
      sanitized[sanitizedKey] = value.length > 100 ? value.substring(0, 100) : value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[sanitizedKey] = value;
    } else {
      // Skip complex types (arrays, objects, null, undefined)
      console.debug(`[FIREBASE] Skipping param "${key}" with unsupported type:`, typeof value);
      continue;
    }

    paramCount++;
  }

  return sanitized;
}

class AppsFlyerService {
  private isInitialized = false;
  private isNativePlatform = false;
  private deepLinkCallback?: (data: any) => void;
  private conversionDataCallback?: (data: any) => void;

  constructor() {
    this.isNativePlatform = Capacitor.isNativePlatform();
  }

  /**
   * Initialize AppsFlyer SDK with deep linking and ATT support
   */
  async initialize(): Promise<void> {
    // Store debug info in localStorage for inspection after app boots
    if (!this.isNativePlatform) {
      console.log('[APPSFLYER] Running on web platform, skipping initialization');
      localStorage.setItem('appsflyer_status', 'skipped_web_platform');
      return;
    }

    if (this.isInitialized) {
      console.log('[APPSFLYER] Already initialized');
      return;
    }

    try {
      localStorage.setItem('appsflyer_status', 'initializing');

      // Validate configuration
      if (!APPSFLYER_CONFIG.devKey || !APPSFLYER_CONFIG.appID) {
        const error = `Missing AppsFlyer configuration - devKey: ${APPSFLYER_CONFIG.devKey ? 'SET' : 'MISSING'}, appID: ${APPSFLYER_CONFIG.appID ? 'SET' : 'MISSING'}`;
        localStorage.setItem('appsflyer_status', 'failed_config');
        localStorage.setItem('appsflyer_error', error);
        throw new Error(error);
      }

      console.log('[APPSFLYER] Starting initialization...');
      console.log('[APPSFLYER] Platform:', Capacitor.getPlatform());
      console.log('[APPSFLYER] Config:', {
        devKey: APPSFLYER_CONFIG.devKey.substring(0, 8) + '...',
        appID: APPSFLYER_CONFIG.appID,
        isDebug: APPSFLYER_CONFIG.isDebug,
      });

      // Set up deep linking listeners BEFORE SDK initialization
      console.log('[APPSFLYER] Setting up deep linking listeners...');
      await this.setupDeepLinkingListeners();

      const config: AFInit = APPSFLYER_CONFIG;

      // Initialize the SDK
      console.log('[APPSFLYER] Calling initSDK...');
      await AppsFlyer.initSDK(config);
      console.log('[APPSFLYER] ✅ initSDK completed');

      // Request App Tracking Transparency (iOS 14+)
      console.log('[APPSFLYER] Requesting ATT permission...');
      await this.requestATTPermission();
      console.log('[APPSFLYER] ✅ ATT permission handled');

      // SDK is now initialized and ready to use
      this.isInitialized = true;
      localStorage.setItem('appsflyer_status', 'initialized');
      localStorage.setItem('appsflyer_initialized_at', new Date().toISOString());
      console.log('[APPSFLYER] ✅ Initialization complete - isInitialized =', this.isInitialized);

      // Set customer ID if available
      await this.autoSetCustomerID();

      // Track app launch
      this.trackEvent('app_launch', {
        platform: Capacitor.getPlatform(),
        launch_time: Date.now(),
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      localStorage.setItem('appsflyer_status', 'failed');
      localStorage.setItem('appsflyer_error', errorMsg);
      localStorage.setItem('appsflyer_failed_at', new Date().toISOString());
      console.error('[APPSFLYER] ❌ Failed to initialize SDK:', error);
      console.error('[APPSFLYER] Error details:', JSON.stringify(error, null, 2));
      // Don't re-throw - AppsFlyer is non-critical, allow app to continue without it
      // This prevents unhandled promise rejections when the native plugin isn't available
      // (e.g., after OTA updates via Capgo where native code wasn't updated)
    }
  }

  /**
   * Request App Tracking Transparency (ATT) permission (iOS 14+)
   * This should be called after initSDK but before startSDK
   */
  private async requestATTPermission(): Promise<void> {
    // Only request ATT on iOS
    if (Capacitor.getPlatform() !== 'ios') {
      console.log('[APPSFLYER] Skipping ATT request - not iOS platform');
      return;
    }

    try {
      console.log('[APPSFLYER] Requesting App Tracking Transparency permission...');
      const result = await AppTrackingTransparency.requestPermission();
      const status = result?.status || 'unavailable';

      console.log('[APPSFLYER] ATT status:', status);

      // Track ATT response
      this.trackEvent('att_permission_response', {
        status,
        platform: 'ios',
      });

      // Log different statuses
      switch (status) {
        case 'authorized':
          console.log('[APPSFLYER] ✅ ATT permission granted - full tracking enabled');
          break;
        case 'denied':
          console.log('[APPSFLYER] ❌ ATT permission denied - limited tracking');
          break;
        case 'restricted':
          console.log('[APPSFLYER] ⚠️ ATT restricted - device/parental controls active');
          break;
        case 'notDetermined':
          console.log('[APPSFLYER] ⏳ ATT not determined - user has not been asked yet');
          break;
        default:
          console.log('[APPSFLYER] ATT status unknown:', status);
      }
    } catch (error) {
      console.error('[APPSFLYER] Error requesting ATT permission:', error);
      // Don't throw - continue with SDK initialization even if ATT fails
    }
  }

  /**
   * Get current ATT status without requesting permission
   */
  async getATTStatus(): Promise<string | null> {
    if (Capacitor.getPlatform() !== 'ios') {
      return null;
    }

    try {
      const result = await AppTrackingTransparency.getStatus();
      const status = result?.status || null;
      console.log('[APPSFLYER] Current ATT status:', status);
      return status;
    } catch (error) {
      console.error('[APPSFLYER] Error getting ATT status:', error);
      return null;
    }
  }

  /**
   * Set up deep linking and conversion data listeners
   * Should be called BEFORE SDK initialization
   */
  private async setupDeepLinkingListeners(): Promise<void> {
    try {
      // Check if the plugin's addListener method is available
      // This can fail if the native plugin isn't properly registered
      if (typeof AppsFlyer.addListener !== 'function') {
        console.warn('[APPSFLYER] Plugin addListener not available, skipping deep link setup');
        return;
      }

      // Unified Deep Linking (UDL) - Recommended approach
      AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (data: OnDeepLink) => {
        console.log('[APPSFLYER] Deep link received:', data);

        // Handle deep link data
        const deepLinkData = data as any;
        if (deepLinkData.deepLinkValue || deepLinkData.deep_link_value) {
          const deepLinkValue = deepLinkData.deepLinkValue || deepLinkData.deep_link_value;
          console.log('[APPSFLYER] Deep link value:', deepLinkValue);

          // Call custom callback if provided
          if (this.deepLinkCallback) {
            this.deepLinkCallback(data);
          }

          // Track deep link event
          this.trackEvent('af_deep_link', {
            deep_link_value: deepLinkValue,
            media_source: deepLinkData.media_source || 'unknown',
            campaign: deepLinkData.campaign || 'unknown',
          });
        }
      });

      // Conversion data listener (for attribution)
      AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (data: OnConversionDataResult) => {
        console.log('[APPSFLYER] Conversion data received:', data);

        // Call custom callback if provided
        if (this.conversionDataCallback) {
          this.conversionDataCallback(data);
        }

        // Track attribution data
        if ((data as any).af_status === 'Non-organic') {
          this.trackEvent('af_first_attribution', {
            media_source: (data as any).media_source || 'unknown',
            campaign: (data as any).campaign || 'unknown',
            af_status: (data as any).af_status,
          });
        }
      });

      console.log('[APPSFLYER] Deep linking listeners set up successfully');
    } catch (error) {
      console.error('[APPSFLYER] Error setting up deep linking listeners:', error);
    }
  }

  /**
   * Register custom deep link callback
   */
  onDeepLink(callback: (data: any) => void): void {
    this.deepLinkCallback = callback;
  }

  /**
   * Register custom conversion data callback
   */
  onConversionData(callback: (data: any) => void): void {
    this.conversionDataCallback = callback;
  }

  /**
   * Track a custom event to both AppsFlyer and Firebase Analytics
   */
  async trackEvent(eventName: string, eventData: AppsflyerEventData = {}): Promise<void> {
    // Only track events after initialization is complete to respect ATT consent
    if (!this.isNativePlatform || !this.isInitialized) {
      console.debug('[APPSFLYER] Skipping event tracking - not initialized or not native platform');
      return;
    }

    // Track to AppsFlyer
    try {
      const event: AFEvent = {
        eventName,
        eventValue: eventData,
      };

      console.log('[APPSFLYER] Tracking event:', eventName, eventData);
      const result = await AppsFlyer.logEvent(event);
      console.log('[APPSFLYER] Event tracked successfully:', result);
    } catch (error) {
      console.error('[APPSFLYER] Failed to track event:', eventName, error);
    }

    // Also send to Firebase Analytics for cross-platform visibility
    // This respects the same initialization gate as AppsFlyer
    try {
      // Sanitize event name and params for Firebase compatibility
      const sanitizedEventName = sanitizeFirebaseEventName(eventName);
      const sanitizedParams = sanitizeFirebaseParams(eventData);

      await FirebaseAnalytics.logEvent({
        name: sanitizedEventName,
        params: sanitizedParams,
      });
      console.log('[FIREBASE] Event tracked:', sanitizedEventName);
    } catch (error) {
      console.debug('[FIREBASE] Failed to track event:', eventName, error);
    }
  }

  /**
   * Track revenue/purchase events
   */
  async trackRevenue(revenue: number, currency = 'USD', additionalData: Partial<RevenueEventData> = {}): Promise<void> {
    const eventData: AppsflyerEventData = {
      af_revenue: revenue,
      af_currency: currency,
    };

    // Add additional data, filtering out undefined values
    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined) {
        eventData[key] = value;
      }
    });

    await this.trackEvent('af_purchase', eventData);
  }

  /**
   * Track subscription events
   */
  async trackSubscription(planName: string, revenue: number, currency = 'USD', period = 'monthly'): Promise<void> {
    await this.trackEvent('subscription_purchase', {
      af_revenue: revenue,
      af_currency: currency,
      subscription_plan: planName,
      subscription_period: period,
      purchase_time: Date.now(),
    });
  }

  /**
   * Track onboarding completion
   */
  async trackOnboardingComplete(motivations: string[], selectedSymbols: string[]): Promise<void> {
    await this.trackEvent('onboarding_complete', {
      motivation_count: motivations.length,
      selected_symbols_count: selectedSymbols.length,
      completion_time: Date.now(),
    });
  }

  /**
   * Track portfolio creation
   */
  async trackPortfolioCreated(symbolCount: number, portfolioType = 'custom'): Promise<void> {
    await this.trackEvent('portfolio_created', {
      symbol_count: symbolCount,
      portfolio_type: portfolioType,
      creation_time: Date.now(),
    });
  }

  /**
   * Track chat interactions
   */
  async trackChatInteraction(messageType = 'user_message'): Promise<void> {
    await this.trackEvent('chat_interaction', {
      message_type: messageType,
      interaction_time: Date.now(),
    });
  }

  /**
   * Track search actions
   */
  async trackSearch(query: string, resultCount = 0): Promise<void> {
    await this.trackEvent('search_performed', {
      query_length: query.length,
      result_count: resultCount,
      search_time: Date.now(),
    });
  }

  /**
   * Track symbol views
   */
  async trackSymbolView(symbol: string, source: string, viewDuration?: number): Promise<void> {
    const eventData: AppsflyerEventData = {
      symbol,
      source, // e.g., 'search', 'portfolio', 'recommendation', 'news', 'direct'
      view_time: Date.now(),
    };

    if (viewDuration) {
      eventData.view_duration = viewDuration;
    }

    await this.trackEvent('symbol_view', eventData);
  }

  /**
   * Get AppsFlyer UID
   */
  async getAppsFlyerUID(): Promise<string | null> {
    if (!this.isNativePlatform || !this.isInitialized) {
      return null;
    }

    try {
      const result = await AppsFlyer.getAppsFlyerUID();
      console.log('[APPSFLYER] UID retrieved:', result.uid);
      return result.uid;
    } catch (error) {
      console.error('[APPSFLYER] Failed to get UID:', error);
      return null;
    }
  }

  /**
   * Set user ID for tracking
   */
  async setUserId(userId: string): Promise<void> {
    if (!this.isNativePlatform || !this.isInitialized) {
      return;
    }

    try {
      await AppsFlyer.setCustomerUserId({ cuid: userId });
      console.log('[APPSFLYER] User ID set:', userId);
    } catch (error) {
      console.error('[APPSFLYER] Failed to set user ID:', error);
    }
  }

  /**
   * Enable/disable user tracking (for privacy compliance)
   */
  async setTrackingEnabled(enabled: boolean): Promise<void> {
    if (!this.isNativePlatform || !this.isInitialized) {
      return;
    }

    try {
      await AppsFlyer.anonymizeUser({ anonymizeUser: !enabled });
      console.log('[APPSFLYER] Tracking enabled:', enabled);
    } catch (error) {
      console.error('[APPSFLYER] Failed to set tracking enabled:', error);
    }
  }

  /**
   * Automatically detect and set customer ID from various sources
   */
  private async autoSetCustomerID(): Promise<void> {
    try {
      // Try to get customer ID from various sources
      let customerID: string | null = null;

      // 1. Check localStorage for user ID
      const storedUserId = localStorage.getItem('userId') || localStorage.getItem('user_id');
      if (storedUserId) {
        customerID = storedUserId;
      }

      // 2. Check for user email (common identifier)
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('user_email');
      if (!customerID && userEmail) {
        customerID = userEmail;
      }

      // 3. Check for other common user identifiers
      const userIdentifiers = ['customerId', 'customer_id', 'accountId', 'account_id', 'bloomUserId', 'bloom_user_id'];

      for (const identifier of userIdentifiers) {
        const value = localStorage.getItem(identifier);
        if (!customerID && value) {
          customerID = value;
          break;
        }
      }

      // 4. Try to get from sessionStorage as backup
      if (!customerID) {
        for (const identifier of ['userId', 'userEmail', 'customerId']) {
          const value = sessionStorage.getItem(identifier);
          if (value) {
            customerID = value;
            break;
          }
        }
      }

      if (customerID) {
        await this.setUserId(customerID);
        console.log('[APPSFLYER] Auto-detected and set customer ID:', customerID);
      } else {
        console.log('[APPSFLYER] No customer ID found in storage');
      }
    } catch (error) {
      console.error('[APPSFLYER] Error auto-setting customer ID:', error);
    }
  }

  /**
   * Manually trigger customer ID detection (call this after user login)
   */
  async refreshCustomerID(): Promise<void> {
    if (!this.isReady()) {
      console.log('[APPSFLYER] SDK not ready, cannot refresh customer ID');
      return;
    }
    await this.autoSetCustomerID();
  }

  /**
   * Set customer ID and store it for persistence
   */
  async setCustomerID(customerID: string, persist = true): Promise<void> {
    if (!customerID) {
      console.warn('[APPSFLYER] Cannot set empty customer ID');
      return;
    }

    // Set in AppsFlyer
    await this.setUserId(customerID);

    // Optionally persist to localStorage for future sessions
    if (persist) {
      try {
        localStorage.setItem('appsflyer_customer_id', customerID);
        console.log('[APPSFLYER] Customer ID persisted to localStorage');
      } catch (error) {
        console.warn('[APPSFLYER] Could not persist customer ID to localStorage:', error);
      }
    }

    // Track customer ID set event
    await this.trackEvent('customer_id_set', {
      customer_id_length: customerID.length,
      is_persisted: persist,
    });
  }

  /**
   * Get current customer ID from AppsFlyer or storage
   */
  getCurrentCustomerID(): string | null {
    // Try to get from localStorage first
    const stored = localStorage.getItem('appsflyer_customer_id');
    if (stored) {
      return stored;
    }

    // Fallback to other common storage keys
    const fallbackKeys = ['userId', 'userEmail', 'customerId'];
    for (const key of fallbackKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        return value;
      }
    }

    return null;
  }

  /**
   * Check if SDK is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized && this.isNativePlatform;
  }
}

// Export singleton instance
export const appsflyerService = new AppsFlyerService();
export default appsflyerService;
